/**
 * Una sola sequenza per il dettaglio di un turno: messaggio, passi dell'harness, tool,
 * chiamate esterne, risposta.
 *
 * Prima la pagina teneva i tool e le chiamate in due elenchi. TravelCore e le fasi della
 * ricerca comparivano come «API» anche quando erano lavoro interno di un tool. Qui stanno
 * sulla stessa linea, con i passi che l'harness registra (smistamento, modello, chiusura).
 * L'ordine è l'orario di inizio, quando c'è; i turni già chiusi non ce l'hanno e restano
 * nell'ordine in cui sono stati salvati.
 */

const timeMs = (valore) => {
  const ms = Date.parse(valore ?? '')
  return Number.isFinite(ms) ? ms : null
}

/** Poll dello stesso servizio, o la stessa ricerca ripetuta sulla stessa ancora. */
const POLL =
  /^(Get(?:Air|Rail|Hotel|Car)Offers|Get(?:City|Car|Airport|Railway|Hotel)LocationPoints)$/
const FASI_RIPETIBILI = /^dinamico_(car|train|hotel|plane)$/

const groupKey = (call) => {
  if (/LocationPoints$/.test(call.method ?? '')) {
    const coords = /(\d+\.\d+,\d+\.\d+)/.exec(call.inputSummary ?? '')
    return coords ? `${call.provider}:punti:${coords[1]}` : `${call.provider}:${call.method}`
  }
  if (POLL.test(call.method ?? '')) return `${call.provider}:${call.method}`
  if (FASI_RIPETIBILI.test(call.method ?? '')) {
    return `${call.provider}:${call.method}:${call.inputSummary ?? ''}`
  }
  return null
}

/**
 * Letture consecutive dello stesso servizio diventano una voce. I poll di un altro
 * servizio, o un passo diverso, chiudono il gruppo: la linea resta cronologica.
 */
const groupCalls = (voci) => {
  const out = []
  for (const voce of voci) {
    const key = voce.kind === 'call' ? groupKey(voce.call) : null
    const last = out[out.length - 1]
    if (key && last?.kind === 'group' && last.key === key) {
      last.calls.push(voce.call)
      continue
    }
    if (key) {
      out.push({ kind: 'group', key, calls: [voce.call], at: voce.at, order: voce.order })
      continue
    }
    out.push(voce)
  }
  return out.map((voce) =>
    voce.kind === 'group' && voce.calls.length === 1
      ? { kind: 'call', call: voce.calls[0], at: voce.at, order: voce.order }
      : voce
  )
}

/**
 * @param {{ userMessage?: string|null, toolCalls?: object[], texts?: string[] }|null} trace
 * @param {{ rawCalls?: object[], harness?: object[] }|null} usage
 * @returns {Array<{ kind: string, at: number|null, order: number }>}
 */
export const turnTimeline = (trace, usage) => {
  const voci = []
  let order = 0
  if (trace?.userMessage) {
    voci.push({ kind: 'user', text: trace.userMessage, at: null, order: order++ })
  }
  for (const step of usage?.harness ?? []) {
    voci.push({ kind: 'harness', step, at: timeMs(step.at), order: order++ })
  }
  for (const call of trace?.toolCalls ?? []) {
    voci.push({ kind: 'tool', call, at: timeMs(call.startedAt), order: order++ })
  }
  for (const call of usage?.rawCalls ?? []) {
    voci.push({ kind: 'call', call, at: timeMs(call.startedAt), order: order++ })
  }
  for (const text of trace?.texts ?? []) {
    if (text) voci.push({ kind: 'reply', text, at: null, order: order++ })
  }

  const datati = voci.filter((voce) => voce.at != null)
  if (datati.length < 2) return groupCalls(voci)
  const primo = Math.min(...datati.map((voce) => voce.at))
  const ultimo = Math.max(...datati.map((voce) => voce.at))
  const ordinati = [...voci].sort((a, b) => {
    const ia = a.at ?? (a.kind === 'user' ? primo - 1 : a.kind === 'reply' ? ultimo + 1 : null)
    const ib = b.at ?? (b.kind === 'user' ? primo - 1 : b.kind === 'reply' ? ultimo + 1 : null)
    if (ia == null || ib == null) return a.order - b.order
    return ia - ib || a.order - b.order
  })
  return groupCalls(ordinati)
}
